"use client";
import { Description } from "@mui/icons-material";
import { Box, Button, Card, CardContent, CardHeader, Typography } from "@mui/material";
import type React from "react";
import { useId } from "react";
import { TERMS_MODAL_STYLES } from "./termStyles";

interface TermsUploadCardProps {
	title: string;
	fileName: string | null;
	content: string;
	isLoading?: boolean;
	onFileSelect: (file: File) => void;
	/** 追加のアクションボタンを表示する場合 */
	actionButtons?: React.ReactNode;
}

export function TermsUploadCard({
	title,
	fileName,
	content,
	isLoading = false,
	onFileSelect,
	actionButtons,
}: TermsUploadCardProps): React.ReactElement {
	const fileInputId = useId();

	return (
		<Card>
			<CardHeader
				title={
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<Typography variant="h6">{title}</Typography>
					</Box>
				}
				sx={{ pb: 1 }}
			/>
			<CardContent sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 1 }}>
				{/* ファイルアップロード */}
				<Box>
					<Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "medium" }}>
						HTMLファイルをアップロード
					</Typography>
					<input
						id={fileInputId}
						type="file"
						accept=".html"
						style={{ display: "none" }}
						onChange={(e) => {
							const file = e.target.files?.[0];
							if (file) {
								onFileSelect(file);
							}
							e.target.value = "";
						}}
					/>
					<label htmlFor={fileInputId}>
						<Button
							variant="outlined"
							color="secondary"
							component="span"
							disabled={isLoading}
							sx={{ minWidth: 200, cursor: "pointer" }}
						>
							{fileName || "ファイルを選択してください"}
						</Button>
					</label>
				</Box>

				{/* 利用規約のHTMLをプレビュー */}
				<Box>
					{content ? (
						<>
							<Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "medium" }}>
								利用規約プレビュー
							</Typography>
							<Box
								sx={{
									border: 1,
									borderColor: "divider",
									borderRadius: 1,
									p: 3,
									bgcolor: "background.paper",
									maxHeight: 600,
									minHeight: 400,
									overflowY: "auto",
									overflowX: "hidden",
								}}
							>
								<iframe
									srcDoc={`${TERMS_MODAL_STYLES}${content}`}
									sandbox="allow-popups allow-popups-to-escape-sandbox"
									title={`${title}プレビュー`}
									width="100%"
									height="500px"
									style={{
										border: "none",
										borderRadius: "4px",
										backgroundColor: "#fafafa",
									}}
								/>
							</Box>
						</>
					) : (
						<Box
							sx={{
								border: 2,
								borderStyle: "dashed",
								borderColor: "grey.300",
								borderRadius: 1,
								p: 4,
								textAlign: "center",
							}}
						>
							<Description sx={{ fontSize: 48, color: "grey.400", mb: 2 }} />
							<Typography variant="body2" color="text.secondary">
								ファイルをアップロードすると、こちらにプレビューが表示されます
							</Typography>
						</Box>
					)}
				</Box>

				{/* 追加のアクションボタン */}
				{actionButtons}
			</CardContent>
		</Card>
	);
}
